import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const COOKIE_KEY = "cookie_consent";
const PREFS_KEY = "bookrtutor_cookie_prefs";
const CONSENT_EXPIRY_DAYS = 365;

interface CookiePrefs {
  analytics: boolean;
  functional: boolean;
}

function setConsentCookie(value: "accepted" | "declined") {
  const d = new Date();
  d.setDate(d.getDate() + CONSENT_EXPIRY_DAYS);
  document.cookie = `${COOKIE_KEY}=${value}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
}

function getConsentCookie(): string | null {
  const match = document.cookie.split(";").find((c) => c.trim().startsWith(`${COOKIE_KEY}=`));
  if (!match) return null;
  return match.split("=")[1]?.trim() || null;
}

function savePrefs(prefs: CookiePrefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

function loadPrefs(): CookiePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { analytics: true, functional: true };
}

const CookieConsent = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>({ analytics: true, functional: true });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);

    const check = async () => {
      // If logged in, check DB first
      if (user) {
        const { data } = await supabase
          .from("profiles")
          .select("cookie_consent")
          .eq("id", user.id)
          .single();

        if (data?.cookie_consent === true) {
          // "accepted" — never show again
          setConsentCookie("accepted");
          setVisible(false);
          setReady(true);
          return;
        }
        // cookie_consent is false or null → show banner every visit
        setVisible(true);
        setPrefs(loadPrefs());
        setReady(true);
        return;
      }

      // Not logged in — check cookie
      const consent = getConsentCookie();
      if (consent === "accepted") {
        setVisible(false);
      } else {
        // "declined", null, or anything else → show banner
        setVisible(true);
        setPrefs(loadPrefs());
      }
      setReady(true);
    };

    check();
  }, [user]);

  const handleAcceptAll = useCallback(async () => {
    const allOn = { analytics: true, functional: true };
    setPrefs(allOn);
    savePrefs(allOn);
    setConsentCookie("accepted");
    setVisible(false);
    setPrefsOpen(false);

    if (user) {
      await supabase
        .from("profiles")
        .update({ cookie_consent: true })
        .eq("id", user.id);
    }
  }, [user]);

  const handleDecline = useCallback(async () => {
    // Declining only disables analytics — functional cookies (dark mode etc.)
    // still work because they are localStorage-based preferences, not cookies.
    // Essential auth cookies are always active (UK PECR exempt).
    const declined = { analytics: false, functional: true };
    setPrefs(declined);
    savePrefs(declined);
    setConsentCookie("declined");
    setVisible(false);
    setPrefsOpen(false);

    if (user) {
      await supabase
        .from("profiles")
        .update({ cookie_consent: false })
        .eq("id", user.id);
    }
  }, [user]);

  const handleSavePrefs = async () => {
    savePrefs(prefs);
    const allAccepted = prefs.analytics && prefs.functional;

    if (allAccepted) {
      setConsentCookie("accepted");
      if (user) {
        await supabase
          .from("profiles")
          .update({ cookie_consent: true })
          .eq("id", user.id);
      }
    } else {
      setConsentCookie("declined");
      if (user) {
        await supabase
          .from("profiles")
          .update({ cookie_consent: false })
          .eq("id", user.id);
      }
    }

    setPrefsOpen(false);
    setVisible(false);
  };

  if (!ready || !visible) return null;

  return (
    <>
      {/* Slim bottom bar */}
      <div className="fixed inset-x-0 bottom-0 z-[60] bg-primary border-t border-primary/20 shadow-lg">
        <div className="container mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-primary-foreground text-sm text-center sm:text-left leading-snug">
            We use essential cookies to keep the site working securely. We'd also like to set optional cookies to help us improve your experience. You can accept or manage your preferences below.{" "}
            <Link to="/privacy" className="underline underline-offset-2 font-medium text-primary-foreground/90 hover:text-primary-foreground">
              Privacy Policy
            </Link>
          </p>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              size="lg"
              onClick={handleAcceptAll}
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-6"
            >
              Got It
            </Button>
            <button
              onClick={() => { setPrefsOpen(true); }}
              className="text-primary-foreground/80 underline underline-offset-2 text-sm hover:text-primary-foreground transition-colors whitespace-nowrap"
            >
              Manage Preferences
            </button>
          </div>
        </div>
      </div>

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              Choose which cookies you allow. Essential cookies are always enabled.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Strictly Necessary</Label>
                <p className="text-xs text-muted-foreground">Required for the site to work.</p>
              </div>
              <Switch checked disabled />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Analytics</Label>
                <p className="text-xs text-muted-foreground">Helps us understand how the site is used.</p>
              </div>
              <Switch
                checked={prefs.analytics}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Functional</Label>
                <p className="text-xs text-muted-foreground">Remembers your preferences.</p>
              </div>
              <Switch
                checked={prefs.functional}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
              />
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Read our{" "}
            <Link to="/privacy" className="text-primary underline underline-offset-2" onClick={() => setPrefsOpen(false)}>
              Privacy Policy
            </Link>
          </p>

          <DialogFooter className="flex-row gap-2 sm:justify-between">
            <Button variant="outline" size="sm" onClick={handleDecline}>
              Reject All
            </Button>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleSavePrefs}>
                Save Preferences
              </Button>
              <Button size="sm" onClick={handleAcceptAll} className="font-semibold">
                Accept All
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
